import React from "react";
import { useTranslation } from "react-i18next";

// Placeholder bar in the app's usual skeleton colour. No default rounding: Bootstrap's
// .rounded is !important and would override the rounded-full / rounded-lg passed in.
const Bar = ({ className = "" }) => <div className={`bg-ui-border ${className}`} />;

// A toggle-sized slot with the track where CarrierToggle draws it.
const ToggleBar = ({ className = "" }) => (
  <div className={`h-[38px] w-[58px] shrink-0 pl-[8px] pt-[8px] ${className}`}>
    <Bar className="h-[22px] w-[42px] rounded-full" />
  </div>
);

const ActionBars = ({ className = "" }) => (
  <div className={`flex gap-1.5 ${className}`}>
    <Bar className="h-7 w-[58px] rounded-full" />
    <Bar className="h-7 w-[72px] rounded-full" />
  </div>
);

// Same container classes as CarrierCard, so it takes the same space.
const SkeletonCard = ({ account }) => (
  <div className="flex flex-col rounded-xl border border-ui-border bg-ui-surface">
    <div className="flex items-start gap-3 pb-2.5 pl-3.5 pr-2 pt-3.5">
      <Bar className="h-11 w-[76px] shrink-0 rounded-lg" />
      <div className="flex min-w-0 flex-1 flex-col gap-[6px] self-center">
        <Bar className="h-[14px] w-3/5 rounded-[4px]" />
        {account && <Bar className="h-[10px] w-2/5 rounded-[4px]" />}
      </div>
      <ToggleBar className="-mt-1" />
    </div>
    <div className="flex items-center justify-between gap-2 border-t border-ui-border-subtle px-3.5 py-[9px]">
      <Bar className="h-[10px] w-[96px] rounded-[4px]" />
      <Bar className="h-[26px] w-[82px] rounded-full" />
    </div>
    {account && <ActionBars className="justify-end px-3.5 pb-2.5" />}
  </div>
);

// Shaped like the phone rows (CarrierRow and the account rows).
const SkeletonRow = ({ account }) => (
  <div className={`pl-[12px] pr-1 ${account ? "py-2" : "py-1.5"}`}>
    <div className="flex items-center gap-2.5">
      <Bar className="h-8 w-14 shrink-0 rounded-md" />
      <div className="flex min-w-0 flex-1 flex-col gap-[6px]">
        <Bar className="h-[12px] w-1/2 rounded-[4px]" />
        <Bar className="h-[10px] w-1/3 rounded-[4px]" />
      </div>
      <ToggleBar />
    </div>
    {account && <ActionBars className="mt-1 pl-[66px]" />}
  </div>
);

// Loading state shared by the Hajex Network and My Carrier Accounts lists: phone rows below
// 640px and cards above, both shaped like the real content.
export const LoadingCards = ({ count = 8, account = false }) => {
  const { t } = useTranslation("settings");
  const keys = Array.from({ length: count }, (_, index) => index);
  return (
    <div role="status" aria-busy="true" className="motion-safe:animate-pulse">
      <span className="sr-only">{t("carriers.loading")}</span>
      <div className="divide-y divide-ui-border-subtle overflow-hidden rounded-xl border border-ui-border bg-ui-surface sm:hidden">
        {keys.map((key) => (
          <SkeletonRow key={key} account={account} />
        ))}
      </div>
      <div className="hidden grid-cols-[repeat(auto-fill,minmax(270px,1fr))] items-start gap-3 sm:grid">
        {keys.map((key) => (
          <SkeletonCard key={key} account={account} />
        ))}
      </div>
    </div>
  );
};

export const EmptyState = ({ children }) => (
  <div className="rounded-xl border border-dashed border-ui-border bg-ui-surface p-5 text-center text-[13px] text-ui-text-muted">
    {children}
  </div>
);
